import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageCircleQuestion, UserRound, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { API_BASE } from '../../config/api';
import QuestionThread, { QUESTION_STATUS } from '../instructor/QuestionThread';

const API = `${API_BASE}/api`;
const fld = 'bg-zinc-900 border-zinc-700 text-white';

export default function AskInstructorCard({ programmeId, token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [info, setInfo] = useState(null);
  const [threads, setThreads] = useState([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null);
  const [form, setForm] = useState({ subject: '', body: '', activity_id: '' });

  const loadThreads = useCallback(() => axios.get(`${API}/programmes/${programmeId}/my-questions`, { headers })
    .then((r) => setThreads(r.data.questions || [])).catch(() => {}), [programmeId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    axios.get(`${API}/programmes/${programmeId}/instructor-info`, { headers }).then((r) => setInfo(r.data)).catch(() => setInfo(null));
    loadThreads();
  }, [programmeId, loadThreads]); // eslint-disable-line react-hooks/exhaustive-deps

  const lead = info?.lead_instructor;
  const courseInstructors = (info?.courses || []).filter((c) => c.instructor && c.instructor.user_id !== lead?.user_id);
  if (!lead && !courseInstructors.length) return null;

  const submit = async () => {
    try {
      await axios.post(`${API}/programmes/${programmeId}/questions`, { ...form, activity_id: form.activity_id || null }, { headers });
      toast.success('Question sent to your instructor');
      setForm({ subject: '', body: '', activity_id: '' });
      loadThreads();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send question'); }
  };

  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5 mb-8 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="ask-instructor-card">
      <div className="h-11 w-11 rounded-full bg-teal-500/15 flex items-center justify-center shrink-0"><UserRound className="h-5 w-5 text-teal-300" /></div>
      <div className="flex-1 min-w-0">
        <p className="text-xs uppercase tracking-[0.15em] text-zinc-500">Your instructor</p>
        {lead ? <p className="text-white font-medium" data-testid="lead-instructor-name">{lead.name} <a href={`mailto:${lead.email}`} className="text-sm text-teal-400 hover:underline ml-1 inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{lead.email}</a></p>
          : <p className="text-zinc-400 text-sm">See course instructors below</p>}
        {courseInstructors.length > 0 && <p className="text-xs text-zinc-500 mt-1 truncate">Course instructors: {courseInstructors.map((c) => `${c.title} — ${c.instructor.name}`).join(' · ')}</p>}
      </div>
      <Button onClick={() => setOpen(true)} className="bg-teal-600 hover:bg-teal-700 text-white shrink-0" data-testid="ask-instructor-btn">
        <MessageCircleQuestion className="h-4 w-4 mr-2" />Ask a question{threads.some((t) => t.status === 'answered') && <span className="ml-2 h-2 w-2 rounded-full bg-amber-400" />}
      </Button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setView(null); }}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-white max-w-xl max-h-[85vh] overflow-y-auto" data-testid="ask-instructor-dialog">
          <DialogHeader><DialogTitle>{view ? 'Your question' : 'Ask your instructor'}</DialogTitle></DialogHeader>
          {view ? (
            <>
              <QuestionThread thread={view} token={token} onUpdated={(t) => { setView(t); loadThreads(); }} />
              <Button variant="ghost" size="sm" onClick={() => setView(null)} className="text-zinc-400" data-testid="ask-back-btn">Back</Button>
            </>
          ) : (
            <div className="space-y-4">
              <select className={`h-9 w-full rounded-md border px-2 text-sm ${fld}`} value={form.activity_id} onChange={(e) => setForm({ ...form, activity_id: e.target.value })} data-testid="ask-course-select">
                <option value="">General programme question</option>
                {(info?.courses || []).map((c) => <option key={c.activity_id} value={c.activity_id}>{c.title}</option>)}
              </select>
              <Input placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={fld} data-testid="ask-subject-input" />
              <Textarea placeholder="What would you like help with?" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className={`${fld} min-h-[110px]`} data-testid="ask-body-input" />
              <Button onClick={submit} disabled={!form.subject.trim() || !form.body.trim()} className="w-full bg-teal-600 hover:bg-teal-700 text-white" data-testid="ask-submit-btn">Send question</Button>
              {threads.length > 0 && (
                <div className="pt-2 border-t border-zinc-800 space-y-2">
                  <p className="text-xs uppercase tracking-wide text-zinc-500">My questions</p>
                  {threads.map((t) => (
                    <button key={t.question_id} onClick={() => setView(t)} className="w-full flex justify-between gap-2 text-left rounded-lg border border-zinc-800 p-2.5 hover:border-zinc-700" data-testid={`my-question-${t.question_id}`}>
                      <span className="text-sm text-zinc-200 truncate">{t.subject}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] shrink-0 ${QUESTION_STATUS[t.status]}`}>{t.status}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
